import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, MessageCircleIcon, CalendarDaysIcon, FacebookIcon, LinkIcon, ClockIcon, UsersIcon } from 'lucide-react';
import { initMetaPixel, trackPageView, trackLeadForPixel, CLASE_PIXEL_ID } from '../utils/metaPixel';
import { trackPage } from '../utils/analytics';
import { initOpenAIPixel, trackOpenAILead } from '../utils/openaiPixel';
const WHATSAPP_URL = 'https://chat.whatsapp.com/GQS3ZwQhhJKB5CTvmvXLeJ';
const REDIRECT_SECONDS = 10;
const SHARE_URL = 'https://webinar.bienesraicesacademy.com/clase-fb';

// 7:30 PM CDMX (UTC-6) on Oct 6 2026 = 01:30 UTC Oct 7
const EVENT_START = '20261007T013000Z';
const EVENT_END = '20261007T030000Z';
const EVENT_TITLE = 'Clase en Vivo: Cómo Construir Múltiples Fuentes de Ingreso en Bienes Raíces';
const EVENT_DETAILS = 'Clase gratuita en vivo con Susana Sánchez G — Academia de Bienes Raíces.';
const EVENT_FACTS = [{
  icon: CalendarDaysIcon,
  label: 'Fecha',
  value: 'Martes, 6 de Octubre de 2026'
}, {
  icon: ClockIcon,
  label: 'Hora',
  value: '7:30 PM (Hora CDMX)'
}, {
  icon: UsersIcon,
  label: 'Modalidad',
  value: 'Online (Enlace vía WhatsApp)'
}];
interface GraciasClaseProps {
  /** Path recorded in analytics so each traffic source is measured separately. */
  trackingPath?: string;
  /** Loads the OpenAI pixel and fires its lead_created conversion. */
  enableOpenAIPixel?: boolean;
  /** Registration page shared via the Facebook and copy-link buttons. */
  shareUrl?: string;
  /** WhatsApp group opened by the button and the auto-redirect. */
  whatsappUrl?: string;
}
export function GraciasClase({
  trackingPath = '/gracias-clase',
  enableOpenAIPixel = false,
  shareUrl = SHARE_URL,
  whatsappUrl = WHATSAPP_URL
}: GraciasClaseProps) {
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [popupBlocked, setPopupBlocked] = useState(false);
  useEffect(() => {
    // Meta Pixel — PageView + Lead
    initMetaPixel();
    trackPageView();
    trackLeadForPixel(CLASE_PIXEL_ID);

    // OpenAI Pixel — lead_created
    if (enableOpenAIPixel) {
      initOpenAIPixel();
      trackOpenAILead();
    }
    trackPage(trackingPath);
  }, [trackingPath, enableOpenAIPixel]);

  // Auto-open WhatsApp in a new tab after countdown (current page stays open)
  useEffect(() => {
    if (countdown <= 0) {
      trackPage('whatsapp_click');
      const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        setPopupBlocked(true);
      }
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, whatsappUrl]);
  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${EVENT_START}/${EVENT_END}&text=${encodeURIComponent(EVENT_TITLE)}&details=${encodeURIComponent(EVENT_DETAILS)}`;
  const outlookCalendarUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&startdt=${EVENT_START}&enddt=${EVENT_END}&subject=${encodeURIComponent(EVENT_TITLE)}&body=${encodeURIComponent(EVENT_DETAILS)}`;
  const downloadIcs = () => {
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${EVENT_START}
DTEND:${EVENT_END}
SUMMARY:${EVENT_TITLE}
DESCRIPTION:${EVENT_DETAILS}
END:VEVENT
END:VCALENDAR`;
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8'
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', 'clase-bienes-raices.ics');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    alert('¡Enlace copiado al portapapeles!');
  };
  return <div className="min-h-screen bg-black font-sans text-white" data-id="element-589" data-mp="3be9238y6ofv3">
      {/* ABOVE THE FOLD — Success + WhatsApp */}
      <section className="px-4 pt-6 md:pt-10 pb-6" data-id="element-590" data-mp="37eql2o9hgoxj">
        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1]
      }} className="max-w-2xl mx-auto rounded-3xl bg-[#0B0B0B] p-6 md:p-10 shadow-2xl text-center border-t-4 border-gold-metallic" data-id="element-591" data-mp="3lcyc6fju8w0s">
          <CheckCircleIcon className="w-14 h-14 md:w-16 md:h-16 text-green-500 mx-auto mb-3" data-id="element-592" data-mp="7wh9ptg4xtwd" />
          <h1 className="font-sans text-2xl md:text-4xl font-bold uppercase mb-2" data-id="element-593" data-mp="2yxr6hcemuym">
            Tu registro está casi completo
          </h1>
          <p className="text-base md:text-lg text-white/70 mb-6" data-id="element-594" data-mp="cwlp1g5b4il0">
            Te esperamos el 6 de octubre a las 7:30pm (Hora CDMX).
          </p>

          <div className="rounded-2xl bg-white/[0.04] p-4 md:p-6 border border-white/10" data-id="element-595" data-mp="win73qjedgo6">
            <h2 className="font-bold text-lg mb-2 text-gold-metallic flex items-center justify-center gap-2" data-id="element-596" data-mp="10ifgu6kj1sv4">
              <UsersIcon className="w-5 h-5" data-id="element-597" data-mp="mjpmuzt089az" />
              Paso Final Obligatorio
            </h2>
            <p className="text-white/70 mb-4 text-sm md:text-base" data-id="element-598" data-mp="rh7mfzl4tc6b">
              Únete al grupo exclusivo de WhatsApp donde enviaremos el enlace de
              acceso a la clase y el material de preparación.
            </p>
            <motion.a whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0 0 0 0 rgba(37, 211, 102, 0.45)', '0 0 0 12px rgba(37, 211, 102, 0)', '0 0 0 0 rgba(37, 211, 102, 0)']
          }} transition={{
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              ease: 'easeOut'
            }
          }} href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => trackPage('whatsapp_click')} className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-colors w-full text-base md:text-lg" data-id="element-599" data-mp="hmpdog0uyqfn">
              <MessageCircleIcon className="w-6 h-6" data-id="element-600" data-mp="3pt2thaid1157" />
              UNIRME AL GRUPO DE WHATSAPP
            </motion.a>

            {popupBlocked ? <p className="mt-3 text-sm font-semibold text-gold-metallic" data-id="element-601" data-mp="3fvps3c4x5oa1">
                Hubo un error en la apertura automática. Por favor haz clic en
                el botón de arriba para unirte al grupo.
              </p> : countdown > 0 ? <p className="mt-3 text-sm text-white/60" data-id="element-602" data-mp="jzjqo1x839cw">
                Se abrirá en una nueva pestaña en{' '}
                <span className="font-bold text-white" data-id="element-603" data-mp="oy3gidext5aj">{countdown}</span>{' '}
                {countdown === 1 ? 'segundo' : 'segundos'}...
              </p> : <p className="mt-3 text-sm text-white/60" data-id="element-604" data-mp="tvwp1d3pqbdz">
                Abriendo WhatsApp en una nueva pestaña...
              </p>}
          </div>
        </motion.div>
      </section>

      {/* BELOW THE FOLD */}
      <div className="max-w-3xl mx-auto px-4 pb-12 space-y-6" data-id="element-605" data-mp="17ol5c6wnfgqm">
        {/* EVENT DETAILS */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="rounded-2xl bg-[#0B0B0B] p-6 md:p-8 border border-white/10" data-id="element-606" data-mp="1cnpfazredii0">
          <h3 className="font-sans text-xl md:text-2xl font-bold uppercase mb-6 border-b border-white/10 pb-4" data-id="element-607" data-mp="1p6jpl8ymvzmf">
            Detalles del Evento
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-608" data-mp="1k7gjrmatxpde">
            {EVENT_FACTS.map(({
            icon: Icon,
            label,
            value
          }) => <div key={label} className="flex items-start gap-3" data-id="element-609" data-mp="1txtbpzkz4ktk">
                <Icon className="w-6 h-6 text-gold-metallic flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-610" data-mp="1tzmkplj27h4q" />
                <div data-id="element-611" data-mp="119nsxidhd5wd">
                  <dt className="text-xs uppercase tracking-wider text-white/50" data-id="element-612" data-mp="11afavqe5n546">
                    {label}
                  </dt>
                  <dd className="font-semibold" data-id="element-613" data-mp="1g3sb10agonqk">{value}</dd>
                </div>
              </div>)}
          </dl>
        </motion.section>

        {/* CALENDAR */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="rounded-2xl bg-[#0B0B0B] p-6 md:p-8 border border-white/10 text-center" data-id="element-614" data-mp="2rjgxzrnptmrd">
          <h3 className="font-sans text-xl md:text-2xl font-bold uppercase mb-6" data-id="element-615" data-mp="2l74m9ot3zlta">
            Agrega el evento a tu calendario
          </h3>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-616" data-mp="3cvpmvmw07fnz">
            <button type="button" onClick={downloadIcs} className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-617" data-mp="3hvavyq4w14ef">
              <CalendarDaysIcon className="w-5 h-5" data-id="element-618" data-mp="3846fflzkdbhh" /> Apple Calendar
            </button>
            <a href={googleCalendarUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-619" data-mp="3vu0r4085cu05">
              <CalendarDaysIcon className="w-5 h-5" data-id="element-620" data-mp="lie4xez098mv" /> Google Calendar
            </a>
            <a href={outlookCalendarUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-621" data-mp="6my66wnhcok5">
              <CalendarDaysIcon className="w-5 h-5" data-id="element-622" data-mp="2kte42cojzzm" /> Outlook
            </a>
          </div>
        </motion.section>

        {/* SHARE */}
        <motion.section initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.35,
        ease: [0.23, 1, 0.32, 1]
      }} className="rounded-2xl bg-[#0B0B0B] p-6 md:p-8 border border-white/10 text-center" data-id="element-623" data-mp="3k2w4r5ydb2tj">
          <h3 className="font-sans text-xl md:text-2xl font-bold uppercase mb-2" data-id="element-624" data-mp="2wdxuqe8ukw9r">
            Invita a tus conocidos
          </h3>
          <p className="text-white/60 mb-6" data-id="element-625" data-mp="35e47tz0mvu8e">
            ¿Conoces a alguien que le interesaría construir múltiples fuentes de
            ingreso?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-626" data-mp="30dyca7r8tnl1">
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#0d65d9] text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-627" data-mp="3fb8wc5hyc1sd">
              <FacebookIcon className="w-5 h-5" data-id="element-628" data-mp="1yoyl002xoob" /> Compartir en Facebook
            </a>
            <button type="button" onClick={copyLink} className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-629" data-mp="3t4q3srxd5o21">
              <LinkIcon className="w-5 h-5" data-id="element-630" data-mp="2uq1f2r82uhnh" /> Copiar Enlace
            </button>
          </div>
        </motion.section>
      </div>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-white/50" data-id="element-631" data-mp="34hnn6ep4c0m0">
        <p data-id="element-632" data-mp="39ha9ddmxcr6y">&copy; 2026 Susana Sánchez G. Todos los derechos reservados.</p>
      </footer>
    </div>;
}